import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import { Card, Form, Button, Space } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import BannerForm from "../../components/banners/BannerForm";
import BannerLangImages, {
  buildBannerPayload,
} from "../../components/banners/BannerLangImages";
import bannerService from "../../services/bannerService";
import masterService from "../../services/masterService";
import { useCommon } from "../../context/CommonContext";

const AddBanner = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const notify = useNotify();
  const { setPageTitle } = useCommon();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    m_banner_type: undefined,
    show_type: undefined,
  });

  const [videoShows, setVideoShows] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [languages, setLanguages] = useState([]);

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    setPageTitle("Add Banner");
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);

      const [
        videoShowsRes,
        tvShowsRes,
        languagesRes,
      ] = await Promise.allSettled([
        masterService.getVideoShows({
          isAll: true,
        }),
        masterService.getTvShows({
          isAll: true,
        }),
        masterService.getVideoLanguages({
          isAll: true,
          status: "Y",
        }),
      ]);

      if (videoShowsRes.status === "fulfilled" && videoShowsRes.value.data) {
        const vsData = videoShowsRes.value.data;
        const list = Array.isArray(vsData?.data?.data)
          ? vsData.data.data
          : Array.isArray(vsData?.data)
          ? vsData.data
          : [];
        setVideoShows(list);
      }

      if (tvShowsRes.status === "fulfilled" && tvShowsRes.value.data) {
        const tvData = tvShowsRes.value.data;
        const list = Array.isArray(tvData?.data?.data)
          ? tvData.data.data
          : Array.isArray(tvData?.data)
          ? tvData.data
          : [];
        setTvShows(list);
      }

      if (languagesRes.status === "fulfilled" && languagesRes.value.data) {
        const langData = languagesRes.value.data;
        const list = Array.isArray(langData?.data?.data)
          ? langData.data.data
          : Array.isArray(langData?.data)
          ? langData.data
          : [];
        setLanguages(list);
      }
    } catch (error) {
      console.error("Error loading dropdown data:", error);
      notify.error({
        message: "Failed to load form data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBannerTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      m_banner_type: value,
      show_type: undefined,
    }));

    // lang_id is reset too: it is the SHOW's language, not the artwork
    // languages, which live on the image sets and survive a type change.
    form.setFieldsValue({
      show_type: undefined,
      show_id: undefined,
      lang_id: undefined,
    });
  };

  const handleShowTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      show_type: value,
    }));

    form.setFieldsValue({
      show_id: undefined,
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // The artwork fields are form-only.
      const response = await bannerService.addBanner(buildBannerPayload(values));

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Banner added successfully!",
        });
        form.resetFields();
        setFormData({
          m_banner_type: undefined,
          show_type: undefined,
        });
        navigate("/banners");
      } else {
        notify.error({
          message: response.data.message || "Failed to add banner",
        });
      }
    } catch (error) {
      console.error("Error adding banner:", error);
      notify.error({
        // The api interceptor rejects with the axios *response*, so the server's
        // own message sits on error.data.
        message:
          error?.data?.message ||
          error?.response?.data?.message ||
          "Failed to add banner",
      });
    } finally {
      setLoading(false);
    }
  };

  // The uploads and redirect URL sit below the fold, so a validation failure
  // there looks like the button doing nothing — name the first offending field.
  const handleSubmitFailed = ({ errorFields }) => {
    const first = errorFields?.[0];
    notify.error({
      message: first?.errors?.[0] || "Please fix the highlighted fields",
    });
  };

  const handleReset = () => {
    form.resetFields();
    setFormData({
      m_banner_type: undefined,
      show_type: undefined,
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleSubmitFailed}
          scrollToFirstError
          initialValues={{
            // Required field; defaulting to Active avoids a validation failure
            // on the very first save.
            banner_is_active: "Y",
            // Per-language artwork, added a set at a time in Media Assets.
            images: [],
          }}
        >
          {/* No languages prop: BannerForm has no language field of its own —
              the list belongs to BannerLangImages, where each image set names
              the language it serves. */}
          <BannerForm
            form={formData}
            videoShows={videoShows}
            tvShows={tvShows}
            onBannerTypeChange={handleBannerTypeChange}
            onShowTypeChange={handleShowTypeChange}
          >
            {/* Not required: a banner can be saved with artwork missing and
                filled in later. */}
            {/* No countries prop: the country picker lives in BannerForm's
                Configuration section and takes its list directly. */}
            <BannerLangImages
              form={form}
              languages={languages}
              bannerType={formData.m_banner_type}
            />
          </BannerForm>

          <Form.Item style={{ marginTop: "32px" }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Add Banner
              </Button>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                size="large"
              >
                Reset
              </Button>

              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
  );
};

export default AddBanner;
